import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Camera, Pencil, Plus, Trash2 } from 'lucide-react';
import { EditIcon, WarningIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import AdminModal from '../../components/AdminModal';
import AdminPageShell from '../../components/AdminPageShell';
import AdminTabs from '../../components/AdminTabs';
import FieldList from '../../components/FieldList';
import AllFieldsSection from '../../components/AllFieldsSection';
import EntityAuditList from '../../components/EntityAuditList';
import SearchSelect, { SearchOption } from '../../components/SearchSelect';
import {
  OFFICE_OPTIONS,
  PARTY_OPTIONS,
  electoralDistrictScopeForOffice,
  officeKeyForOfficeSought,
} from '../../../constants/candidates';
import { HelpTrigger } from '../../../help';
import CandidateProfileCards from './CandidateProfileCards';
import { formatDate } from '../../lib/format';
import { searchPersons } from '../../lib/pickers';
import { padDistrictNumber } from '../../../utils/districtDisplay';
import AddressAutocomplete from '../../../components/AddressAutocomplete';

interface PersonDetail {
  person: Record<string, unknown> & {
    id: string;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    middle_name: string | null;
    suffix: string | null;
    preferred_name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
    photo_url?: string;
  };
  contacts: Array<{
    id: string;
    contact_type: string;
    contact_value: string;
    context: string | null;
    is_primary: boolean;
    platform: string | null;
    handle: string | null;
  }>;
  addresses: Array<
    Record<string, unknown> & {
      id: string;
      address_type: string | null;
      address_line_1: string | null;
      address_line_2: string | null;
      city: string | null;
      state: string | null;
      zip_code: string | null;
      is_primary: boolean | null;
    }
  >;
  candidacies: Array<{
    id: string;
    status: string;
    office_sought: string | null;
    district: string | null;
    district_display: string | null;
    party_affiliation: string | null;
    election_year: number | null;
    election_type: string | null;
    sos_candidate_id: string | null;
    campaign_finance_committee_name: string | null;
    is_incumbent: boolean | null;
    withdrawal_reason: string | null;
    level: string | null;
    sos_jurisdiction: string | null;
    affidavit_filed_date: string | null;
    candidate_status_sos: string | null;
    campaign_status_sos: string | null;
    voluntary_spending_limits: boolean | null;
    ballotpedia_url: string | null;
    endorsement_notes: string | null;
    result_notes: string | null;
    withdrawal_date: string | null;
    electoral_district_id: string | null;
    contest_id: string | null;
    contest_display: string | null;
    /** Ticket (§5.9): the governor this Lt. Governor candidacy runs with … */
    running_mate_candidate_id: string | null;
    running_mate_display: string | null;
    /** … and, on a governor candidacy, the Lt. Governor pointing at it. */
    lt_governor_display: string | null;
  }>;
  officeholderTerms: Array<{
    id: string;
    title: string | null;
    role_subtype: string | null;
    jurisdiction: string | null;
    status: string | null;
    start_date: string | null;
    end_date: string | null;
    electoral_district_id: string | null;
    district_display: string | null;
    campaign_finance_committee_name: string | null;
  }>;
  sessions: Array<{
    id: number;
    session_year: string | null;
    chamber: string | null;
    district: number | null;
    party_affiliation: string | null;
    status: string | null;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'roles', label: 'Roles' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'activity', label: 'Activity' },
];

interface AgentBundle {
  person: { id: string; full_name: string | null; primary_email: string | null } | null;
}

interface CampaignDetail extends Record<string, unknown> {
  id: string;
  campaign_finance_committee_name: string | null;
  finance_committee:
    | (Record<string, unknown> & {
        id: string;
        name: string | null;
        sos_committee_id: string | null;
        /* numeric in Postgres, so the pg driver returns it as a string */
        beginning_balance: string | number | null;
        purpose?: string | null;
        /* In the campaign finance import → edited in the Committees tool, read-only here. */
        in_import?: boolean;
        imported_name?: string | null;
        registered_agent: AgentBundle | null;
        designated_filing_agent: AgentBundle | null;
      })
    | null;
  staff: Array<{
    id: string;
    person_id: string;
    full_name: string;
    role_type: string;
    role_title: string | null;
    status: string | null;
    start_date: string | null;
    end_date: string | null;
  }>;
}

interface FinanceData {
  totals: {
    total_raised: string | null;
    contribution_count: string | null;
    last_transaction_date: string | null;
  };
  contributions: Array<{
    contributor_name: string;
    amount: string;
    transaction_date: string;
  }>;
}

const STAFF_ROLE_TYPES = [
  'campaign_manager', 'treasurer', 'deputy_manager', 'finance_director',
  'communications_director', 'field_director', 'volunteer_coordinator',
  'consultant', 'advisor', 'other',
];
const STAFF_STATUSES = ['active', 'inactive', 'terminated', 'resigned'];

const roleTypeLabel = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatUsd = (value: string | number | null | undefined) => {
  /*
    null / undefined / '' mean "not recorded" and must not render as $0.00 —
    Beginning Balance treats a stored 0 as a deliberate zero carry-in, distinct
    from never having been entered. Number(null) is 0, so without this guard the
    two become indistinguishable on screen.
  */
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const statusChip = (status: string | null | undefined) =>
  status ? (
    <span className="inline-flex rounded-full bg-background-secondary px-2 py-0.5 text-xs font-medium text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
      {status}
    </span>
  ) : null;

const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

/** Manually selectable contexts — legacy/import-only values ('primary', finance agents) excluded. */
const CONTACT_CONTEXTS = ['personal', 'official', 'campaign', 'district', 'firm', 'temporary'];
const CONTACT_TYPES = ['email', 'phone', 'fax', 'website', 'social_media'];
const ADDRESS_TYPES = ['mailing', 'home', 'office', 'campaign', 'district', 'capitol'];
const ELECTION_TYPES = ['general', 'primary', 'special', 'runoff'];

const EMPTY_TERM_FORM = { id: '', title: '', role_subtype: '', jurisdiction: '', start_date: '', end_date: '', status: 'active', electoral_district_id: '' };
const EMPTY_CANDIDACY_FORM = {
  id: '',
  office_sought: '',
  district: '',
  party_affiliation: '',
  election_year: '',
  election_type: 'general',
  status: 'active',
  sos_candidate_id: '',
  withdrawal_reason: '',
  sos_jurisdiction: '',
  affidavit_filed_date: '',
  candidate_status_sos: '',
  campaign_status_sos: '',
  /** Tri-state: '' = unset (NULL), 'true' / 'false' = explicit. */
  voluntary_spending_limits: '',
  ballotpedia_url: '',
  endorsement_notes: '',
  result_notes: '',
  withdrawal_date: '',
  /** Which seat (electoral_districts.id) — required to reach a contest for by-district offices. */
  electoral_district_id: '',
  /** '' = link automatically on save; a uuid = an explicitly chosen contest. */
  contest_id: '',
  /** Lt. Governor candidacies only: the governor candidacy this one runs with (§5.9.4). */
  running_mate_candidate_id: '',
};
/** Officeholder term jurisdictions — the two governments whose offices the catalog holds. */
/** 'FEDERAL' is the established value for federal terms (Angie, 2026-09-15); 'Colorado' is what the legislator importer writes. */
const TERM_JURISDICTIONS = ['Colorado', 'FEDERAL'];
const EMPTY_PERSON_FORM = { first_name: '', last_name: '', middle_name: '', suffix: '', preferred_name: '', primary_email: '', primary_phone: '' };
const EMPTY_CONTACT_FORM = { id: '', contact_type: 'email', contact_value: '', context: 'personal', platform: '', handle: '', is_primary: false };
const EMPTY_ADDRESS_FORM = { id: '', address_type: 'mailing', address_line_1: '', address_line_2: '', city: '', state: 'CO', zip_code: '', is_primary: false };
const EMPTY_STAFF_FORM = { id: '', role_type: 'campaign_manager', role_title: '', status: 'active' };

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ??
  error?.response?.data?.message ??
  error?.response?.data?.errors?.[0]?.msg ??
  fallback;

/**
 * Person detail — the role timeline (Documentation/PEOPLE_TOOL_PLAN.md §3.2).
 * P2: every section edits in place (no global edit flag) — identity/photo,
 * contacts, addresses, candidacies, officeholder terms. Campaign fields,
 * merge, and reconcile stay in the legacy tool until P3.
 */
const PeoplePersonDetailPage: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('overview');
  const [termModal, setTermModal] = useState<'add' | 'edit' | null>(null);
  const [termForm, setTermForm] = useState(EMPTY_TERM_FORM);
  const [candidacyModal, setCandidacyModal] = useState<'add' | 'edit' | null>(null);
  const [candidacyForm, setCandidacyForm] = useState(EMPTY_CANDIDACY_FORM);
  const [personEditOpen, setPersonEditOpen] = useState(false);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON_FORM);
  const [contactModal, setContactModal] = useState<'add' | 'edit' | null>(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT_FORM);
  const [deleteContactTarget, setDeleteContactTarget] = useState<{ id: string; label: string } | null>(null);
  const [addressModal, setAddressModal] = useState<'add' | 'edit' | null>(null);
  const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS_FORM);
  const [deleteAddressTarget, setDeleteAddressTarget] = useState<{ id: string; label: string } | null>(null);
  const [campaignCandidacyId, setCampaignCandidacyId] = useState('');
  const [committeeModalOpen, setCommitteeModalOpen] = useState(false);
  const [committeeForm, setCommitteeForm] = useState({ name: '', sos_committee_id: '', beginning_balance: '' });
  const [registeredAgent, setRegisteredAgent] = useState<SearchOption | null>(null);
  const [designatedAgent, setDesignatedAgent] = useState<SearchOption | null>(null);
  const [staffModal, setStaffModal] = useState<'add' | 'edit' | null>(null);
  const [staffForm, setStaffForm] = useState(EMPTY_STAFF_FORM);
  const [staffPerson, setStaffPerson] = useState<SearchOption | null>(null);
  const [removeStaffTarget, setRemoveStaffTarget] = useState<{ id: string; name: string } | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const candidateStatuses = useQuery({
    queryKey: ['role-statuses', 'candidate'],
    queryFn: async () => {
      const response = await api.get('/role-statuses', { params: { role_type: 'candidate' } });
      const rows = (response.data.data ?? response.data ?? []) as Array<{ value: string; label?: string }>;
      return rows.map((row) => row.value);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Officeholder term titles come from the offices catalog (the Elections
  // domain's source of truth), never free text.
  const offices = useQuery({
    queryKey: ['offices-catalog'],
    queryFn: async () => {
      const response = await api.get('/contests/offices');
      return (response.data.data ?? []) as Array<{
        id: string; display_label: string; scope: string; district_scope: string | null; office_level: string | null;
      }>;
    },
    staleTime: 5 * 60 * 1000,
    enabled: termModal !== null,
  });

  // Keep a legacy/imported title selectable when editing a term whose office
  // predates the catalog.
  const officeLabels = (offices.data ?? []).map((office) => office.display_label);

  /**
   * A by-district office needs to say WHICH seat. The catalog carries the scope
   * and the district scope, so the picker only appears for offices that have
   * districts, and only offers that office's own districts.
   */
  const selectedOffice = (offices.data ?? []).find((office) => office.display_label === termForm.title);
  const districtScope = selectedOffice?.scope === 'by_district' ? selectedOffice.district_scope : null;

  const termDistricts = useQuery({
    queryKey: ['electoral-districts', districtScope],
    enabled: Boolean(districtScope),
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const response = await api.get('/reference/electoral-districts', {
        params: { state_code: 'CO', scope: districtScope },
      });
      return (response.data.data?.districts ?? []) as Array<{
        id: string; display_label: string; district_number: number | null;
      }>;
    },
  });
  const termOfficeOptions =
    termForm.title && !officeLabels.includes(termForm.title)
      ? [termForm.title, ...officeLabels]
      : officeLabels;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-people', 'person', personId] });
    queryClient.invalidateQueries({ queryKey: ['admin-list'] });
    queryClient.invalidateQueries({ queryKey: ['admin-accounts', 'audit', 'person', personId ?? ''] });
  };

  const candidacyStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/admin/candidates/${id}/status`, { status }),
    onSuccess: () => { toast.success('Candidacy status updated'); invalidate(); },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update status')),
  });

  const termSaveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        title: termForm.title,
        role_subtype: termForm.role_subtype || null,
        jurisdiction: termForm.jurisdiction || null,
        start_date: termForm.start_date || undefined,
        end_date: termForm.end_date || undefined,
        status: termForm.status,
        // Which seat, not just which office. Without it a US House term records
        // "U.S. House" with no district, and no citizen lookup can match it.
        electoral_district_id: termForm.electoral_district_id || null,
      };
      return termModal === 'add'
        ? api.post(`/persons/${personId}/roles/officeholder`, payload)
        : api.put(`/persons/${personId}/roles/${termForm.id}`, payload);
    },
    onSuccess: () => {
      toast.success(termModal === 'add' ? 'Officeholder term added' : 'Term updated');
      setTermModal(null);
      setTermForm(EMPTY_TERM_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save term')),
  });

  const candidacySaveMutation = useMutation({
    mutationFn: () => {
      const year = parseInt(candidacyForm.election_year, 10);
      if (candidacyModal === 'add') {
        return api.post('/candidates', {
          person_id: personId,
          office_sought: candidacyForm.office_sought,
          district: candidacyForm.district.trim() || null,
          electoral_district_id: candidacySelectedDistrictId || null,
          contest_id: candidacyEffectiveContestId || null,
          running_mate_candidate_id: candidacyIsLtGovernor ? candidacyForm.running_mate_candidate_id || undefined : undefined,
          party_affiliation: candidacyForm.party_affiliation || undefined,
          election_year: Number.isFinite(year) ? year : undefined,
          election_type: candidacyForm.election_type,
          status: candidacyForm.status,
        });
      }
      return api.put(`/candidates/${candidacyForm.id}`, {
        office_sought: candidacyForm.office_sought,
        district: candidacyForm.district.trim() || null,
        electoral_district_id: candidacySelectedDistrictId || null,
        // '' asks the API to (re)link automatically from office + district.
        contest_id: candidacyEffectiveContestId,
        // Only a Lt. Governor candidacy carries the ticket link; anything else clears it.
        running_mate_candidate_id: candidacyIsLtGovernor ? candidacyForm.running_mate_candidate_id || null : null,
        party_affiliation: candidacyForm.party_affiliation || null,
        election_year: Number.isFinite(year) ? year : undefined,
        election_type: candidacyForm.election_type,
        sos_candidate_id: candidacyForm.sos_candidate_id.trim() || null,
        withdrawal_reason: candidacyForm.withdrawal_reason.trim() || null,
        sos_jurisdiction: candidacyForm.sos_jurisdiction.trim() || null,
        affidavit_filed_date: candidacyForm.affidavit_filed_date || null,
        candidate_status_sos: candidacyForm.candidate_status_sos.trim() || null,
        campaign_status_sos: candidacyForm.campaign_status_sos.trim() || null,
        voluntary_spending_limits:
          candidacyForm.voluntary_spending_limits === ''
            ? null
            : candidacyForm.voluntary_spending_limits === 'true',
        ballotpedia_url: candidacyForm.ballotpedia_url.trim() || null,
        endorsement_notes: candidacyForm.endorsement_notes.trim() || null,
        result_notes: candidacyForm.result_notes.trim() || null,
        withdrawal_date: candidacyForm.withdrawal_date || null,
      });
    },
    onSuccess: () => {
      toast.success(candidacyModal === 'add' ? 'Candidacy added' : 'Candidacy updated');
      setCandidacyModal(null);
      setCandidacyForm(EMPTY_CANDIDACY_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save candidacy')),
  });

  const personMutation = useMutation({
    mutationFn: () =>
      api.put(`/persons/${personId}`, {
        first_name: personForm.first_name.trim() || undefined,
        last_name: personForm.last_name.trim() || undefined,
        middle_name: personForm.middle_name.trim(),
        suffix: personForm.suffix.trim(),
        preferred_name: personForm.preferred_name.trim(),
        primary_email: personForm.primary_email.trim(),
        primary_phone: personForm.primary_phone.trim(),
      }),
    onSuccess: () => {
      toast.success('Person updated');
      setPersonEditOpen(false);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to update person')),
  });

  const photoMutation = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('photo', file);
      return api.put(`/persons/${personId}/upload-photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => { toast.success('Photo updated'); invalidate(); },
    onError: (error: any) => toast.error(apiError(error, 'Failed to upload photo')),
  });

  const contactSaveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        contact_value: contactForm.contact_value.trim(),
        context: contactForm.context,
        is_primary: contactForm.is_primary,
      };
      if (contactForm.contact_type === 'social_media') {
        payload.platform = contactForm.platform.trim() || undefined;
        payload.handle = contactForm.handle.trim() || undefined;
      }
      return contactModal === 'add'
        ? api.post(`/admin/contacts/person/${personId}/contact-method`, {
            ...payload,
            contact_type: contactForm.contact_type,
          })
        : api.put(`/admin/contacts/contact-method/${contactForm.id}`, payload);
    },
    onSuccess: () => {
      toast.success(contactModal === 'add' ? 'Contact added' : 'Contact updated');
      setContactModal(null);
      setContactForm(EMPTY_CONTACT_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save contact')),
  });

  const contactDeleteMutation = useMutation({
    mutationFn: (contactId: string) => api.delete(`/admin/contacts/contact-method/${contactId}`),
    onSuccess: () => {
      toast.success('Contact deleted');
      setDeleteContactTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete contact')),
  });

  const addressSaveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        address_line_1: addressForm.address_line_1.trim(),
        address_line_2: addressForm.address_line_2.trim(),
        city: addressForm.city.trim(),
        state: addressForm.state.trim(),
        zip_code: addressForm.zip_code.trim(),
        is_primary: addressForm.is_primary,
      };
      return addressModal === 'add'
        ? api.post(`/admin/contacts/person/${personId}/address`, {
            ...payload,
            address_type: addressForm.address_type,
          })
        : api.put(`/admin/contacts/address/${addressForm.id}`, payload);
    },
    onSuccess: () => {
      toast.success(addressModal === 'add' ? 'Address added' : 'Address updated');
      setAddressModal(null);
      setAddressForm(EMPTY_ADDRESS_FORM);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save address')),
  });

  const addressDeleteMutation = useMutation({
    mutationFn: (addressId: string) => api.delete(`/admin/contacts/address/${addressId}`),
    onSuccess: () => {
      toast.success('Address deleted');
      setDeleteAddressTarget(null);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete address')),
  });

  // Candidacy years come from configured election cycles, never free input
  // (ELECTIONS_TOOL_PLAN.md E2 — POST /candidates is strict about this).
  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles', 'CO'],
    queryFn: async () => {
      const response = await api.get('/election-calendars', { params: { state: 'CO' } });
      return (response.data.data ?? []) as Array<{
        id: string;
        election_year: number;
        election_type: string;
        name: string | null;
      }>;
    },
    staleTime: 5 * 60 * 1000,
    enabled: candidacyModal !== null,
  });

  // Ballot linkage for the candidacy form: which seat, then which contest.
  // The citizen ballot resolves candidates by contest_id alone, so a candidacy
  // saved without one is invisible however its status reads.
  const candidacyDistrictScope = electoralDistrictScopeForOffice(candidacyForm.office_sought);
  const candidacyDistricts = useQuery({
    queryKey: ['electoral-districts', candidacyDistrictScope],
    enabled: candidacyModal !== null && Boolean(candidacyDistrictScope),
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const response = await api.get('/reference/electoral-districts', {
        params: { state_code: 'CO', scope: candidacyDistrictScope },
      });
      return (response.data.data?.districts ?? []) as Array<{
        id: string; display_label: string; district_number: number | null;
      }>;
    },
  });
  const candidacyCalendarId =
    (cycles.data ?? []).find(
      (cycle) => cycle.election_type === 'regular' && String(cycle.election_year) === candidacyForm.election_year
    )?.id ?? null;
  const candidacyContests = useQuery({
    queryKey: ['admin-elections', 'cycle-contests', candidacyCalendarId],
    enabled: candidacyModal !== null && Boolean(candidacyCalendarId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const response = await api.get(`/election-calendars/${candidacyCalendarId}/contests`);
      return (response.data.data ?? []) as Array<{
        id: string; display_label: string | null; election_type: string; party: string | null;
        office_key: string | null; electoral_district_id: string | null;
      }>;
    },
  });
  // Governor / Lt. Governor ticket (§5.9.4): the governor candidacies a
  // Lt. Governor candidacy may run with — same cycle, same election type.
  const candidacyIsLtGovernor = officeKeyForOfficeSought(candidacyForm.office_sought) === 'lt_governor';
  const runningMateOptions = useQuery({
    queryKey: ['admin-candidates', 'running-mate-options', candidacyCalendarId, candidacyForm.election_type],
    enabled: candidacyModal !== null && candidacyIsLtGovernor && Boolean(candidacyCalendarId),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await api.get('/admin/candidates/running-mate-options', {
        params: { election_calendar_id: candidacyCalendarId, election_type: candidacyForm.election_type },
      });
      return (response.data.data ?? []) as Array<{
        id: string; name: string; party: string | null; status: string; running_mate_display: string | null;
      }>;
    },
  });

  const detail = useQuery({
    queryKey: ['admin-people', 'person', personId],
    queryFn: async () => {
      const response = await api.get(`/admin/people/${personId}`);
      return response.data.data as PersonDetail;
    },
    enabled: !!personId,
  });

  // Campaign tab is scoped to one candidacy at a time (PEOPLE_TOOL_PLAN.md §3.2).
  const activeCandidacyId =
    campaignCandidacyId || detail.data?.candidacies?.[0]?.id || '';

  const campaign = useQuery({
    queryKey: ['admin-people', 'campaign', activeCandidacyId],
    queryFn: async () => {
      const response = await api.get(`/admin/candidates/${activeCandidacyId}`);
      return response.data.data as CampaignDetail;
    },
    enabled: tab === 'campaign' && activeCandidacyId !== '',
  });

  const finance = useQuery({
    queryKey: ['admin-people', 'campaign-finance', activeCandidacyId],
    queryFn: async () => {
      const response = await api.get(`/candidates/${activeCandidacyId}/campaign-finance`);
      return (response.data.data ?? null) as FinanceData | null;
    },
    enabled: tab === 'campaign' && activeCandidacyId !== '',
  });

  const invalidateCampaign = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-people', 'campaign', activeCandidacyId] });
    queryClient.invalidateQueries({ queryKey: ['admin-people', 'campaign-finance', activeCandidacyId] });
    invalidate();
  };

  // A committee in the campaign finance import is edited in the Committees tool; here
  // it can only be re-linked by TRACER id (Campaign Committee Registry PRD §5.7).
  const committeeInImport = Boolean(campaign.data?.finance_committee?.in_import);

  const committeeSaveMutation = useMutation({
    mutationFn: () =>
      api.put(`/candidates/${activeCandidacyId}`, committeeInImport ? {
        // link_only: the server refuses an id that isn't in the import instead of
        // falling back to the full committee upsert, which would blank that row's fields.
        finance_committee: { sos_committee_id: committeeForm.sos_committee_id.trim() || undefined, link_only: true },
      } : {
        finance_committee: {
          id: campaign.data?.finance_committee?.id ?? undefined,
          name: committeeForm.name.trim() || undefined,
          sos_committee_id: committeeForm.sos_committee_id.trim() || undefined,
          /*
            Admin-entered carry-in for the current cycle (see
            Documentation/CANDIDATE_COMMITTEE_BEGINNING_BALANCE.md).

            Blank sends nothing, which upsertFinanceCommittee normalizes to NULL —
            "not set", which it treats as distinct from 0. So clearing the box
            unsets the figure, and typing 0 records a deliberate zero carry-in.
            Sending '' would land in the same NULL branch; undefined just keeps
            the key out of the request body entirely.
          */
          beginning_balance:
            committeeForm.beginning_balance.trim() === ''
              ? undefined
              : Number(committeeForm.beginning_balance),
          registered_agent_person_id: registeredAgent?.id ?? null,
          designated_filing_agent_person_id: designatedAgent?.id ?? null,
        },
      }),
    onSuccess: () => {
      toast.success('Committee updated');
      setCommitteeModalOpen(false);
      invalidateCampaign();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save committee')),
  });

  const staffSaveMutation = useMutation({
    mutationFn: () =>
      staffModal === 'add'
        ? api.post(`/candidates/${activeCandidacyId}/staff`, {
            person_id: staffPerson?.id,
            role_type: staffForm.role_type,
            role_title: staffForm.role_title.trim() || undefined,
          })
        : api.put(`/candidates/${activeCandidacyId}/staff/${staffForm.id}`, {
            role_type: staffForm.role_type,
            role_title: staffForm.role_title.trim() || null,
            status: staffForm.status,
          }),
    onSuccess: () => {
      toast.success(staffModal === 'add' ? 'Staff member added' : 'Staff role updated');
      setStaffModal(null);
      setStaffForm(EMPTY_STAFF_FORM);
      setStaffPerson(null);
      invalidateCampaign();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save staff role')),
  });

  const staffRemoveMutation = useMutation({
    mutationFn: (staffId: string) => api.delete(`/candidates/${activeCandidacyId}/staff/${staffId}`),
    onSuccess: () => {
      toast.success('Staff member removed');
      setRemoveStaffTarget(null);
      invalidateCampaign();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to remove staff member')),
  });

  if (detail.isLoading) {
    return (
      <AdminPageShell title="Person" backUrl="/admin-next/people">
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading person…</p>
      </AdminPageShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AdminPageShell title="Person" backUrl="/admin-next/people">
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn't load this person.</p>
          <Button variant="outline" size="sm" onClick={() => detail.refetch()}>
            Retry
          </Button>
        </div>
      </AdminPageShell>
    );
  }

  const { person, contacts, addresses, candidacies, officeholderTerms, sessions } = detail.data;
  const name =
    person.full_name || [person.first_name, person.last_name].filter(Boolean).join(' ') || person.id;

  const openPersonEdit = () => {
    setPersonForm({
      first_name: person.first_name ?? '',
      last_name: person.last_name ?? '',
      middle_name: person.middle_name ?? '',
      suffix: person.suffix ?? '',
      preferred_name: person.preferred_name ?? '',
      primary_email: person.primary_email ?? '',
      primary_phone: person.primary_phone ?? '',
    });
    setPersonEditOpen(true);
  };

  const openCommitteeEdit = () => {
    const committee = campaign.data?.finance_committee;
    setCommitteeForm({
      name: committee?.name ?? campaign.data?.campaign_finance_committee_name ?? '',
      sos_committee_id: committee?.sos_committee_id ?? '',
      // Empty string, not "0", when unset — the two mean different things here:
      // blank leaves the stored value alone, 0 is a deliberate zero carry-in.
      beginning_balance:
        committee?.beginning_balance === null || committee?.beginning_balance === undefined
          ? ''
          : String(committee.beginning_balance),
    });
    setRegisteredAgent(
      committee?.registered_agent?.person
        ? {
            id: committee.registered_agent.person.id,
            label: committee.registered_agent.person.full_name || committee.registered_agent.person.id,
          }
        : null
    );
    setDesignatedAgent(
      committee?.designated_filing_agent?.person
        ? {
            id: committee.designated_filing_agent.person.id,
            label: committee.designated_filing_agent.person.full_name || committee.designated_filing_agent.person.id,
          }
        : null
    );
    setCommitteeModalOpen(true);
  };

  const officeOptions = (current: string) =>
    current && !OFFICE_OPTIONS.includes(current) ? [current, ...OFFICE_OPTIONS] : OFFICE_OPTIONS;
  const partyOptions = (current: string) =>
    current && !PARTY_OPTIONS.includes(current) ? [current, ...PARTY_OPTIONS] : PARTY_OPTIONS;
  const termJurisdictionOptions = (current: string) =>
    current && !TERM_JURISDICTIONS.includes(current) ? [current, ...TERM_JURISDICTIONS] : TERM_JURISDICTIONS;

  // Seat for the candidacy: the stored FK, or — for rows that predate the FK —
  // the district whose number matches the free-text district.
  const candidacyDistrictNumber = parseInt((candidacyForm.district.match(/(\d+)/) ?? [])[1] ?? '', 10);
  const candidacySelectedDistrictId =
    candidacyForm.electoral_district_id
    || (candidacyDistricts.data ?? []).find((d) => d.district_number === candidacyDistrictNumber)?.id
    || '';
  // Contests this candidacy could belong on: same office, same district (or
  // statewide), general vs primary per the election type.
  const candidacyOfficeKey = officeKeyForOfficeSought(candidacyForm.office_sought);
  const candidacyContestType = candidacyForm.election_type === 'primary' ? 'primary' : 'general';
  const candidacyContestOptions = (candidacyContests.data ?? []).filter((contest) =>
    contest.office_key === candidacyOfficeKey
    && contest.election_type === candidacyContestType
    && (!candidacyDistrictScope || contest.electoral_district_id === (candidacySelectedDistrictId || null)));
  const candidacyAutoContest =
    candidacyContestOptions.find((c) => candidacyContestType === 'general' ? c.party == null : c.party === candidacyForm.party_affiliation)
    ?? (candidacyContestOptions.length === 1 ? candidacyContestOptions[0] : null);
  // A picked contest only counts while it is still one of the options for the
  // current cycle/office/district/type/party; otherwise the save falls back to
  // automatic linking. One rule, instead of a reset in every field's onChange.
  const candidacyEffectiveContestId =
    candidacyContestOptions.some((c) => c.id === candidacyForm.contest_id) ? candidacyForm.contest_id : '';
  // Candidacies link to the regular cycle for their year; a candidacy whose
  // year predates the cycle catalog stays selectable when editing.
  const regularCycles = (cycles.data ?? []).filter((cycle) => cycle.election_type === 'regular');
  const candidacyCycleOptions = [
    ...new Set([
      ...(candidacyModal === 'edit' && /^\d{4}$/.test(candidacyForm.election_year)
        ? [parseInt(candidacyForm.election_year, 10)]
        : []),
      ...regularCycles.map((cycle) => cycle.election_year),
    ]),
  ]
    .sort((a, b) => b - a)
    .map((year) => ({
      year,
      label: regularCycles.find((cycle) => cycle.election_year === year)?.name || String(year),
    }));
  const candidacySaveDisabled =
    candidacyForm.office_sought.trim() === '' ||
    (candidacyModal === 'add' && !/^\d{4}$/.test(candidacyForm.election_year.trim()));

  return (
    <AdminPageShell
      title={String(name)}
      description="Person"
      backUrl="/admin-next/people"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
        {person.photo_url && (
          <img src={person.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" />
        )}
        <span>
          {candidacies.length} candidac{candidacies.length === 1 ? 'y' : 'ies'} ·{' '}
          {officeholderTerms.length} officeholder term{officeholderTerms.length === 1 ? '' : 's'} ·{' '}
          {sessions.length} legislative session{sessions.length === 1 ? '' : 's'}
        </span>
      </div>

      <AdminTabs tabs={TABS} activeKey={tab} onChange={setTab} />

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="card space-y-4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Identity</h4>
              <div className="flex items-center gap-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  aria-label="Upload photo"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) photoMutation.mutate(file);
                    event.target.value = '';
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Camera}
                  loading={photoMutation.isLoading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {person.photo_url ? 'Change Photo' : 'Add Photo'}
                </Button>
                <Button variant="ghost" size="sm" icon={Pencil} onClick={openPersonEdit}>
                  Edit
                </Button>
              </div>
            </div>
            <FieldList
              fields={[
                { label: 'ID', value: <span className="font-mono text-xs">{person.id}</span>, copyValue: person.id },
                { label: 'Full Name', value: person.full_name ?? '—' },
                { label: 'Preferred Name', value: person.preferred_name ?? '—' },
                { label: 'Primary Email', value: person.primary_email ?? '—' },
                { label: 'Primary Phone', value: person.primary_phone ?? '—' },
              ]}
            />
            <AllFieldsSection record={person} />
          </div>

          <div className="card p-5">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                Contact methods
              </h4>
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => { setContactForm(EMPTY_CONTACT_FORM); setContactModal('add'); }}
              >
                Add Contact
              </Button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No contact methods.</p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {contacts.map((contact) => (
                  <li key={contact.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                    <span className="w-24 text-xs uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                      {contact.platform || contact.contact_type}
                    </span>
                    <span className="flex-1 text-text-primary dark:text-text-dark-primary">
                      {contact.handle || contact.contact_value}
                      {contact.context && (
                        <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          ({contact.context})
                        </span>
                      )}
                    </span>
                    {contact.is_primary && statusChip('primary')}
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Pencil}
                      ariaLabel={`Edit ${contact.contact_type} ${contact.contact_value}`}
                      onClick={() => {
                        setContactForm({
                          id: contact.id,
                          contact_type: contact.contact_type,
                          contact_value: contact.contact_value ?? '',
                          context: contact.context && CONTACT_CONTEXTS.includes(contact.context) ? contact.context : 'personal',
                          platform: contact.platform ?? '',
                          handle: contact.handle ?? '',
                          is_primary: !!contact.is_primary,
                        });
                        setContactModal('edit');
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      ariaLabel={`Delete ${contact.contact_type} ${contact.contact_value}`}
                      onClick={() =>
                        setDeleteContactTarget({
                          id: contact.id,
                          label: `${contact.contact_type} "${contact.handle || contact.contact_value}"`,
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="card p-5">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Addresses</h4>
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                onClick={() => { setAddressForm(EMPTY_ADDRESS_FORM); setAddressModal('add'); }}
              >
                Add Address
              </Button>
            </div>
            {addresses.length === 0 ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No addresses.</p>
            ) : (
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {addresses.map((address) => (
                  <li key={address.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                    <span className="w-24 text-xs uppercase tracking-wide text-text-tertiary dark:text-text-dark-tertiary">
                      {address.address_type || 'address'}
                    </span>
                    <span className="flex-1 text-text-secondary dark:text-text-dark-secondary">
                      {[address.address_line_1, address.address_line_2, address.city, address.state, address.zip_code]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </span>
                    {address.is_primary && statusChip('primary')}
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Pencil}
                      ariaLabel="Edit address"
                      onClick={() => {
                        setAddressForm({
                          id: address.id,
                          address_type: address.address_type ?? 'mailing',
                          address_line_1: address.address_line_1 ?? '',
                          address_line_2: address.address_line_2 ?? '',
                          city: address.city ?? '',
                          state: address.state ?? '',
                          zip_code: address.zip_code ?? '',
                          is_primary: !!address.is_primary,
                        });
                        setAddressModal('edit');
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Trash2}
                      ariaLabel="Delete address"
                      onClick={() =>
                        setDeleteAddressTarget({
                          id: address.id,
                          label: [address.address_type, address.address_line_1, address.city].filter(Boolean).join(' '),
                        })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'roles' && (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" icon={Plus}
              onClick={() => { setCandidacyForm(EMPTY_CANDIDACY_FORM); setCandidacyModal('add'); }}>
              Add Candidacy
            </Button>
            <Button variant="outline" size="sm" icon={Plus}
              onClick={() => { setTermForm(EMPTY_TERM_FORM); setTermModal('add'); }}>
              Add Officeholder Term
            </Button>
          </div>
          {officeholderTerms.map((term) => (
            <div key={term.id} className="card border-l-4 border-warning-500 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                  Officeholder — {term.title || 'Office'} {term.district_display ? `· ${term.district_display}` : ''}
                </h4>
                <div className="flex items-center gap-2">
                  {statusChip(term.status)}
                  <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit term"
                    onClick={() => {
                      setTermForm({
                        id: term.id,
                        title: term.title ?? '',
                        role_subtype: term.role_subtype ?? '',
                        jurisdiction: term.jurisdiction ?? '',
                        start_date: term.start_date ? term.start_date.slice(0, 10) : '',
                        end_date: term.end_date ? term.end_date.slice(0, 10) : '',
                        status: term.status ?? 'active',
                        electoral_district_id: term.electoral_district_id ?? '',
                      });
                      setTermModal('edit');
                    }}>
                    Edit
                  </Button>
                </div>
              </div>
              <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">
                {term.role_subtype ? `${term.role_subtype} · ` : ''}
                {formatDate(term.start_date)} – {term.end_date ? formatDate(term.end_date) : 'present'}
                {term.jurisdiction ? ` · ${term.jurisdiction}` : ''}
              </p>
              {term.campaign_finance_committee_name && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  Committee: {term.campaign_finance_committee_name}
                </p>
              )}
            </div>
          ))}

          {candidacies.map((candidacy) => (
            <div key={candidacy.id} className="card border-l-4 border-primary-600 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                  Candidate — {candidacy.office_sought || 'Office'}{' '}
                  {candidacy.district_display ?? candidacy.district ? `· ${candidacy.district_display ?? candidacy.district}` : ''}
                </h4>
                <div className="flex items-center gap-2">
                  <select
                    value={candidacy.status}
                    onChange={(event) => candidacyStatusMutation.mutate({ id: candidacy.id, status: event.target.value })}
                    aria-label="Candidacy status"
                    className="h-8 rounded-lg border border-border-light bg-white px-2 text-xs text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
                  >
                    {[...new Set([candidacy.status, ...(candidateStatuses.data ?? [])])].map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                  <Button variant="ghost" size="sm" icon={Pencil} ariaLabel="Edit candidacy"
                    onClick={() => {
                      setCandidacyForm({
                        id: candidacy.id,
                        office_sought: candidacy.office_sought ?? '',
                        district: candidacy.district ?? '',
                        party_affiliation: candidacy.party_affiliation ?? '',
                        election_year: candidacy.election_year != null ? String(candidacy.election_year) : '',
                        election_type: candidacy.election_type ?? 'general',
                        status: candidacy.status,
                        sos_candidate_id: candidacy.sos_candidate_id ?? '',
                        withdrawal_reason: candidacy.withdrawal_reason ?? '',
                        sos_jurisdiction: candidacy.sos_jurisdiction ?? '',
                        affidavit_filed_date: candidacy.affidavit_filed_date ? candidacy.affidavit_filed_date.slice(0, 10) : '',
                        candidate_status_sos: candidacy.candidate_status_sos ?? '',
                        campaign_status_sos: candidacy.campaign_status_sos ?? '',
                        voluntary_spending_limits:
                          candidacy.voluntary_spending_limits == null
                            ? ''
                            : String(candidacy.voluntary_spending_limits),
                        ballotpedia_url: candidacy.ballotpedia_url ?? '',
                        endorsement_notes: candidacy.endorsement_notes ?? '',
                        result_notes: candidacy.result_notes ?? '',
                        withdrawal_date: candidacy.withdrawal_date ? candidacy.withdrawal_date.slice(0, 10) : '',
                        electoral_district_id: candidacy.electoral_district_id ?? '',
                        contest_id: candidacy.contest_id ?? '',
                        running_mate_candidate_id: candidacy.running_mate_candidate_id ?? '',
                      });
                      setCandidacyModal('edit');
                    }}>
                    Edit
                  </Button>
                </div>
              </div>
              <p className="mt-1 text-sm text-text-secondary dark:text-text-dark-secondary">
                {candidacy.election_year ?? '—'}
                {candidacy.election_type ? ` ${candidacy.election_type}` : ''}
                {candidacy.party_affiliation ? ` · ${candidacy.party_affiliation}` : ''}
                {candidacy.level ? ` · ${candidacy.level}` : ''}
                {candidacy.is_incumbent ? ' · incumbent' : ''}
              </p>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {candidacy.contest_display ? (
                  <>Ballot: {candidacy.contest_display}</>
                ) : (
                  <span className="inline-flex items-center gap-1 text-warning-700 dark:text-warning-300">
                    <WarningIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Not linked to a contest — not on any ballot
                  </span>
                )}
              </p>
              {officeKeyForOfficeSought(candidacy.office_sought ?? '') === 'lt_governor' && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {candidacy.running_mate_display ? (
                    <>Running with: {candidacy.running_mate_display}</>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-warning-700 dark:text-warning-300">
                      <WarningIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      No running mate — not on any ballot line
                    </span>
                  )}
                </p>
              )}
              {candidacy.lt_governor_display && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  Running mate: {candidacy.lt_governor_display}
                </p>
              )}
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {candidacy.sos_candidate_id ? `SOS ${candidacy.sos_candidate_id} · ` : ''}
                {candidacy.campaign_finance_committee_name || 'No committee linked'}
                {candidacy.withdrawal_reason ? ` · Withdrawn: ${candidacy.withdrawal_reason}` : ''}
              </p>
            </div>
          ))}

          {sessions.length > 0 && (
            <div className="card p-5">
              <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                Legislative sessions (directory)
              </h4>
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {sessions.map((session) => (
                  <li key={session.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {session.session_year}
                    </span>
                    <span className="text-text-secondary dark:text-text-dark-secondary">
                      {session.chamber} {session.district ? `District ${padDistrictNumber(session.district)}` : ''}
                    </span>
                    <span className="text-text-tertiary dark:text-text-dark-tertiary">
                      {session.party_affiliation ?? ''}
                    </span>
                    {statusChip(session.status)}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Roster rows from the legislator directory import — read-only evidence of service.
              </p>
            </div>
          )}

          {officeholderTerms.length === 0 && candidacies.length === 0 && sessions.length === 0 && (
            <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No roles recorded for this person.</p>
          )}
        </div>
      )}

      {tab === 'campaign' &&
        (candidacies.length === 0 ? (
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
            No candidacies — add one from the Roles tab to manage campaign data.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="campaign-candidacy" className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Candidacy
              </label>
              <select
                id="campaign-candidacy"
                value={activeCandidacyId}
                onChange={(event) => setCampaignCandidacyId(event.target.value)}
                className="h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
              >
                {candidacies.map((candidacy) => (
                  <option key={candidacy.id} value={candidacy.id}>
                    {candidacy.election_year ?? '—'} · {candidacy.office_sought || 'Office'} ({candidacy.status})
                  </option>
                ))}
              </select>
            </div>

            {campaign.isLoading ? (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading campaign…</p>
            ) : campaign.isError ? (
              <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
                <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
                <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
                  Couldn't load campaign data.
                </p>
                <Button variant="outline" size="sm" onClick={() => campaign.refetch()}>
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <div className="card p-5">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                      Finance committee
                    </h4>
                    <div className="flex flex-wrap items-center gap-1">
                      {committeeInImport && campaign.data?.finance_committee?.sos_committee_id && (
                        <Link
                          to={`/admin-next/committees/${encodeURIComponent(campaign.data.finance_committee.sos_committee_id)}`}
                          className="inline-flex items-center gap-1 px-2 text-sm text-primary-600 hover:underline dark:text-primary-400"
                        >
                          <EditIcon className="h-4 w-4" aria-hidden /> Edit in Committees
                        </Link>
                      )}
                      <Button variant="ghost" size="sm" icon={committeeInImport ? undefined : Pencil} onClick={openCommitteeEdit}>
                        {committeeInImport ? 'Change committee' : 'Edit'}
                      </Button>
                    </div>
                  </div>
                  {committeeInImport && (
                    <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      This committee is in the campaign finance import, so its details are edited in
                      Committees and shared by every candidate and measure linked to it.
                    </p>
                  )}
                  {campaign.data?.finance_committee ? (
                    <FieldList
                      fields={[
                        {
                          label: 'Committee Name',
                          value:
                            campaign.data.finance_committee.imported_name ??
                            campaign.data.finance_committee.name ??
                            campaign.data.campaign_finance_committee_name ??
                            '—',
                        },
                        ...(committeeInImport
                          ? [{
                              label: 'Purpose',
                              value: campaign.data.finance_committee.purpose || '—',
                              fullWidth: true,
                              wrap: 'words' as const,
                            }]
                          : []),
                        { label: 'SOS Committee ID', value: campaign.data.finance_committee.sos_committee_id ?? '—' },
                        /*
                          Shown here, not only in the Edit dialog: without a row on
                          the card an admin enters a figure, saves, and the card looks
                          identical — indistinguishable from a save that failed.
                          formatUsd renders '—' when unset, so "not recorded" stays
                          visibly different from a deliberate $0.00.
                        */
                        {
                          label: 'Beginning Balance',
                          value: formatUsd(campaign.data.finance_committee.beginning_balance),
                        },
                        {
                          label: 'Registered Agent',
                          value: campaign.data.finance_committee.registered_agent?.person?.full_name ?? '—',
                        },
                        {
                          label: 'Designated Filing Agent',
                          value: campaign.data.finance_committee.designated_filing_agent?.person?.full_name ?? '—',
                        },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                      No committee linked
                      {campaign.data?.campaign_finance_committee_name
                        ? ` — imported name: ${campaign.data.campaign_finance_committee_name}`
                        : ''}
                      .
                    </p>
                  )}
                </div>

                <div className="card p-5">
                  <h4 className="mb-2 text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                    Live campaign finance
                  </h4>
                  {finance.isLoading ? (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading totals…</p>
                  ) : finance.data?.totals ? (
                    <FieldList
                      fields={[
                        { label: 'Total Raised', value: formatUsd(finance.data.totals.total_raised) },
                        { label: 'Contributions', value: finance.data.totals.contribution_count ?? '—' },
                        {
                          label: 'Last Transaction',
                          value: finance.data.totals.last_transaction_date
                            ? formatDate(finance.data.totals.last_transaction_date)
                            : '—',
                        },
                      ]}
                    />
                  ) : (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                      No contribution data — link a committee to see live totals.
                    </p>
                  )}
                  <p className="mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    Totals come from imported TRACER contributions matched by committee name.
                  </p>
                </div>

                <div className="card p-5">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
                      Campaign staff
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={Plus}
                      onClick={() => {
                        setStaffForm(EMPTY_STAFF_FORM);
                        setStaffPerson(null);
                        setStaffModal('add');
                      }}
                    >
                      Add Staff
                    </Button>
                  </div>
                  {(campaign.data?.staff ?? []).length === 0 ? (
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No campaign staff recorded.</p>
                  ) : (
                    <ul className="divide-y divide-border-light dark:divide-border-dark">
                      {(campaign.data?.staff ?? []).map((member) => (
                        <li key={member.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                          <span className="font-medium text-text-primary dark:text-text-dark-primary">
                            {member.full_name}
                          </span>
                          <span className="flex-1 text-text-secondary dark:text-text-dark-secondary">
                            {roleTypeLabel(member.role_type)}
                            {member.role_title ? ` — ${member.role_title}` : ''}
                          </span>
                          {statusChip(member.status)}
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Pencil}
                            ariaLabel={`Edit staff role for ${member.full_name}`}
                            onClick={() => {
                              setStaffForm({
                                id: member.id,
                                role_type: member.role_type,
                                role_title: member.role_title ?? '',
                                status: member.status ?? 'active',
                              });
                              setStaffPerson({ id: member.person_id, label: member.full_name });
                              setStaffModal('edit');
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={Trash2}
                            ariaLabel={`Remove ${member.full_name} from staff`}
                            onClick={() => setRemoveStaffTarget({ id: member.id, name: member.full_name })}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Citizen profile, own words, endorsements, positions, documents (CANDIDATE_PROFILES_PRD.md §5.4.1) */}
                {activeCandidacyId && <CandidateProfileCards candidateId={activeCandidacyId} />}

                {campaign.data && <AllFieldsSection record={campaign.data} />}
              </>
            )}
          </div>
        ))}

      {tab === 'activity' && personId && <EntityAuditList entityType="person" entityId={personId} />}

      <AdminModal
        isOpen={termModal !== null}
        title={termModal === 'add' ? 'Add Officeholder Term' : 'Edit Officeholder Term'}
        onClose={() => setTermModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setTermModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={termForm.title.trim().length === 0}
              loading={termSaveMutation.isLoading} onClick={() => termSaveMutation.mutate()}>
              Save Term
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="term-title" className={labelClasses}>Office *</label>
            <select
              id="term-title"
              value={termForm.title}
              onChange={(event) => {
                const title = event.target.value;
                const office = (offices.data ?? []).find((o) => o.display_label === title);
                // Jurisdiction follows the office; a hand-picked value survives an office change
                // only if it still fits (a federal office never sits under Colorado).
                const derived = office?.office_level === 'federal' ? 'FEDERAL' : 'Colorado';
                setTermForm((current) => ({
                  ...current,
                  title,
                  jurisdiction: office ? derived : current.jurisdiction,
                  electoral_district_id: office?.scope === 'by_district' ? current.electoral_district_id : '',
                }));
              }}
              className={inputClasses}
            >
              <option value="">Select an office…</option>
              {termOfficeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Office not listed?{' '}
              <Link to="/admin-next/elections?tab=offices" className="text-primary-700 hover:underline dark:text-primary-400">
                Add it to the Offices catalog
              </Link>{' '}
              first, then reopen this dialog.
            </p>
          </div>
          {districtScope && (
            <div>
              <label htmlFor="term-district" className={labelClasses}>District *</label>
              <select
                id="term-district"
                value={termForm.electoral_district_id}
                onChange={(event) =>
                  setTermForm((current) => ({ ...current, electoral_district_id: event.target.value }))
                }
                className={inputClasses}
              >
                <option value="">Select a district…</option>
                {(termDistricts.data ?? []).map((district) => (
                  <option key={district.id} value={district.id}>{district.display_label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Which seat this person holds. Citizens are matched to their representative
                by this district.
              </p>
            </div>
          )}
          <div>
            <label htmlFor="term-role_subtype" className={labelClasses}>Party</label>
            <select id="term-role_subtype" value={termForm.role_subtype}
              onChange={(event) => setTermForm((current) => ({ ...current, role_subtype: event.target.value }))}
              className={inputClasses}>
              <option value="">No party</option>
              {partyOptions(termForm.role_subtype).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="term-jurisdiction" className={labelClasses}>Jurisdiction</label>
            <select id="term-jurisdiction" value={termForm.jurisdiction}
              onChange={(event) => setTermForm((current) => ({ ...current, jurisdiction: event.target.value }))}
              className={inputClasses}>
              <option value="">Select…</option>
              {termJurisdictionOptions(termForm.jurisdiction).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Set from the office: federal offices are FEDERAL, everything else Colorado.
            </p>
          </div>
          {([
            { key: 'start_date', label: 'Start date', type: 'date' },
            { key: 'end_date', label: 'End date', type: 'date' },
          ] as const).map((field) => (
            <div key={field.key}>
              <label htmlFor={`term-${field.key}`} className={labelClasses}>
                {field.label}
              </label>
              <input id={`term-${field.key}`} type={field.type} value={termForm[field.key]}
                onChange={(event) => setTermForm((current) => ({ ...current, [field.key]: event.target.value }))}
                className={inputClasses} />
            </div>
          ))}
          {termModal === 'edit' && (
            <div>
              <label htmlFor="term-status" className={labelClasses}>
                Status
              </label>
              <select id="term-status" value={termForm.status}
                onChange={(event) => setTermForm((current) => ({ ...current, status: event.target.value }))}
                className={inputClasses}>
                <option value="active">active</option>
                <option value="ended">ended</option>
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Set status to ended (with an end date) to close this term — history is carried by the dates.
              </p>
            </div>
          )}
          {termModal === 'add' && (
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Adding an active term automatically ends the previous holder of the same seat.
            </p>
          )}
        </div>
      </AdminModal>

      <AdminModal
        isOpen={candidacyModal !== null}
        title={candidacyModal === 'add' ? 'Add Candidacy' : 'Edit Candidacy'}
        onClose={() => setCandidacyModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setCandidacyModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={candidacySaveDisabled}
              loading={candidacySaveMutation.isLoading} onClick={() => candidacySaveMutation.mutate()}>
              Save Candidacy
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="candidacy-office" className={labelClasses}>Office Sought *</label>
            <select id="candidacy-office" value={candidacyForm.office_sought}
              onChange={(event) => setCandidacyForm((current) => ({
                ...current,
                office_sought: event.target.value,
                // A picked contest, seat and running mate belong to the previous office.
                contest_id: '',
                electoral_district_id: '',
                running_mate_candidate_id: '',
                district: electoralDistrictScopeForOffice(event.target.value) ? current.district : '',
              }))}
              className={inputClasses}>
              <option value="">Select an office…</option>
              {officeOptions(candidacyForm.office_sought).map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="candidacy-year" className={labelClasses}>
                Election Cycle {candidacyModal === 'add' ? '*' : ''}
              </label>
              <select id="candidacy-year" value={candidacyForm.election_year}
                onChange={(event) => setCandidacyForm((current) => ({ ...current, election_year: event.target.value, contest_id: '', running_mate_candidate_id: '' }))}
                className={inputClasses}>
                <option value="">Select a cycle…</option>
                {candidacyCycleOptions.map((option) => (
                  <option key={option.year} value={String(option.year)}>{option.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Cycle not listed?{' '}
                <Link to="/admin-next/elections" className="text-primary-700 hover:underline dark:text-primary-400">
                  Add it in Elections → Cycles
                </Link>
                .
              </p>
            </div>
            <div>
              <label htmlFor="candidacy-type" className={labelClasses}>Election Type</label>
              <select id="candidacy-type" value={candidacyForm.election_type}
                onChange={(event) => setCandidacyForm((current) => ({ ...current, election_type: event.target.value, contest_id: '', running_mate_candidate_id: '' }))}
                className={inputClasses}>
                {ELECTION_TYPES.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {candidacyDistrictScope ? (
              <div>
                <label htmlFor="candidacy-district" className={labelClasses}>District *</label>
                <select id="candidacy-district" value={candidacySelectedDistrictId}
                  onChange={(event) => {
                    const picked = (candidacyDistricts.data ?? []).find((d) => d.id === event.target.value);
                    setCandidacyForm((current) => ({
                      ...current,
                      electoral_district_id: event.target.value,
                      district: picked?.district_number != null ? String(picked.district_number) : current.district,
                      contest_id: '',
                    }));
                  }}
                  className={inputClasses}>
                  <option value="">Select a district…</option>
                  {(candidacyDistricts.data ?? []).map((district) => (
                    <option key={district.id} value={district.id}>{district.display_label}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label htmlFor="candidacy-district" className={labelClasses}>District</label>
                <input id="candidacy-district" type="text" value={candidacyForm.district}
                  onChange={(event) => setCandidacyForm((current) => ({ ...current, district: event.target.value }))}
                  className={inputClasses} placeholder="Statewide — leave blank" />
              </div>
            )}
            <div>
              <label htmlFor="candidacy-party" className={labelClasses}>Party</label>
              <select id="candidacy-party" value={candidacyForm.party_affiliation}
                onChange={(event) => setCandidacyForm((current) => ({ ...current, party_affiliation: event.target.value }))}
                className={inputClasses}>
                <option value="">No party</option>
                {partyOptions(candidacyForm.party_affiliation).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1">
              <label htmlFor="candidacy-contest" className={labelClasses}>Contest (ballot)</label>
              <HelpTrigger contentKey="candidacy-ballot-link" size="sm" ariaLabel="How a candidacy reaches a ballot" />
            </div>
            <select id="candidacy-contest" value={candidacyEffectiveContestId}
              onChange={(event) => setCandidacyForm((current) => ({ ...current, contest_id: event.target.value }))}
              className={inputClasses}
              disabled={!candidacyCalendarId || !candidacyOfficeKey || (Boolean(candidacyDistrictScope) && !candidacySelectedDistrictId)}>
              <option value="">
                {candidacyAutoContest
                  ? `Automatic — ${candidacyAutoContest.display_label ?? 'matched contest'}`
                  : 'Automatic — match by office and district on save'}
              </option>
              {candidacyContestOptions.map((contest) => (
                <option key={contest.id} value={contest.id}>
                  {contest.display_label ?? contest.id}{contest.party ? ` (${contest.party} primary)` : ''}
                </option>
              ))}
            </select>
            {candidacyCalendarId && candidacyOfficeKey && (!candidacyDistrictScope || candidacySelectedDistrictId)
              && candidacyContests.isSuccess && candidacyContestOptions.length === 0 && (
              <p className="mt-1 text-xs text-warning-700 dark:text-warning-300">
                No {candidacyContestType} contest exists for this office and district in this cycle. The candidacy will not
                appear on ballots until one is created in{' '}
                <Link to={`/admin-next/elections/cycles/${candidacyCalendarId}`} className="underline">Elections → Cycles</Link>
                {' '}and the candidacy is saved again.
              </p>
            )}
          </div>
          {candidacyIsLtGovernor && (
            <div>
              <label htmlFor="candidacy-running-mate" className={labelClasses}>Running mate (Governor candidacy)</label>
              <select id="candidacy-running-mate" value={candidacyForm.running_mate_candidate_id}
                onChange={(event) => setCandidacyForm((current) => ({ ...current, running_mate_candidate_id: event.target.value }))}
                className={inputClasses}
                disabled={!candidacyCalendarId}>
                <option value="">No running mate — not on any ballot line</option>
                {(runningMateOptions.data ?? []).map((governor) => {
                  const onAnotherTicket = Boolean(governor.running_mate_display) && governor.id !== candidacyForm.running_mate_candidate_id;
                  return (
                    <option key={governor.id} value={governor.id} disabled={onAnotherTicket}>
                      {governor.name}{governor.party ? ` (${governor.party})` : ''}
                      {onAnotherTicket ? ` — running with ${governor.running_mate_display}` : ''}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                A Lieutenant Governor candidacy appears on the citizen ballot only on its governor&apos;s line.
                {runningMateOptions.isSuccess && runningMateOptions.data.length === 0
                  ? ' No governor candidacy exists in this cycle yet — add the governor first.'
                  : ''}
              </p>
            </div>
          )}
          {candidacyModal === 'add' && (
            <div>
              <label htmlFor="candidacy-status" className={labelClasses}>Status</label>
              <select id="candidacy-status" value={candidacyForm.status}
                onChange={(event) => setCandidacyForm((current) => ({ ...current, status: event.target.value }))}
                className={inputClasses}>
                {[...new Set(['active', ...(candidateStatuses.data ?? [])])].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>
          )}
          {candidacyModal === 'edit' && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="candidacy-sos" className={labelClasses}>SOS Candidate ID</label>
                  <input id="candidacy-sos" type="text" value={candidacyForm.sos_candidate_id}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, sos_candidate_id: event.target.value }))}
                    className={inputClasses} />
                </div>
                <div>
                  <label htmlFor="candidacy-sos-jurisdiction" className={labelClasses}>SOS Jurisdiction</label>
                  <input id="candidacy-sos-jurisdiction" type="text" value={candidacyForm.sos_jurisdiction}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, sos_jurisdiction: event.target.value }))}
                    className={inputClasses} />
                </div>
                <div>
                  <label htmlFor="candidacy-status-sos" className={labelClasses}>Candidate Status (SOS)</label>
                  <input id="candidacy-status-sos" type="text" value={candidacyForm.candidate_status_sos}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, candidate_status_sos: event.target.value }))}
                    className={inputClasses} />
                </div>
                <div>
                  <label htmlFor="candidacy-campaign-status-sos" className={labelClasses}>Campaign Status (SOS)</label>
                  <input id="candidacy-campaign-status-sos" type="text" value={candidacyForm.campaign_status_sos}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, campaign_status_sos: event.target.value }))}
                    className={inputClasses} />
                </div>
                <div>
                  <label htmlFor="candidacy-affidavit" className={labelClasses}>Affidavit Filed Date</label>
                  <input id="candidacy-affidavit" type="date" value={candidacyForm.affidavit_filed_date}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, affidavit_filed_date: event.target.value }))}
                    className={inputClasses} />
                </div>
                <div>
                  <label htmlFor="candidacy-spending-limits" className={labelClasses}>Voluntary Spending Limits</label>
                  <select id="candidacy-spending-limits" value={candidacyForm.voluntary_spending_limits}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, voluntary_spending_limits: event.target.value }))}
                    className={inputClasses}>
                    <option value="">Not recorded</option>
                    <option value="true">Accepted</option>
                    <option value="false">Declined</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="candidacy-ballotpedia" className={labelClasses}>Ballotpedia URL</label>
                <input id="candidacy-ballotpedia" type="url" value={candidacyForm.ballotpedia_url}
                  onChange={(event) => setCandidacyForm((current) => ({ ...current, ballotpedia_url: event.target.value }))}
                  className={inputClasses} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="candidacy-withdrawal" className={labelClasses}>Withdrawal Reason</label>
                  <input id="candidacy-withdrawal" type="text" value={candidacyForm.withdrawal_reason}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, withdrawal_reason: event.target.value }))}
                    className={inputClasses} />
                  <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    Set the candidacy status to withdrawn via the status dropdown on the role card.
                  </p>
                </div>
                <div>
                  <label htmlFor="candidacy-withdrawal-date" className={labelClasses}>Withdrawal Date</label>
                  <input id="candidacy-withdrawal-date" type="date" value={candidacyForm.withdrawal_date}
                    onChange={(event) => setCandidacyForm((current) => ({ ...current, withdrawal_date: event.target.value }))}
                    className={inputClasses} />
                </div>
              </div>
              <div>
                <label htmlFor="candidacy-endorsement-notes" className={labelClasses}>Endorsement Notes</label>
                <textarea id="candidacy-endorsement-notes" rows={3} value={candidacyForm.endorsement_notes}
                  onChange={(event) => setCandidacyForm((current) => ({ ...current, endorsement_notes: event.target.value }))}
                  className={inputClasses} />
              </div>
              <div>
                <label htmlFor="candidacy-result-notes" className={labelClasses}>Result Notes</label>
                <textarea id="candidacy-result-notes" rows={3} value={candidacyForm.result_notes}
                  onChange={(event) => setCandidacyForm((current) => ({ ...current, result_notes: event.target.value }))}
                  className={inputClasses} />
              </div>
            </>
          )}
        </div>
      </AdminModal>

      <AdminModal
        isOpen={personEditOpen}
        title="Edit Person"
        onClose={() => setPersonEditOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setPersonEditOpen(false)}>Cancel</Button>
            <Button variant="primary" size="sm"
              disabled={personForm.first_name.trim() === '' || personForm.last_name.trim() === ''}
              loading={personMutation.isLoading} onClick={() => personMutation.mutate()}>
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {([
              { key: 'first_name', label: 'First Name *' },
              { key: 'last_name', label: 'Last Name *' },
              { key: 'middle_name', label: 'Middle Name' },
              { key: 'suffix', label: 'Suffix' },
            ] as const).map((field) => (
              <div key={field.key}>
                <label htmlFor={`person-${field.key}`} className={labelClasses}>{field.label}</label>
                <input id={`person-${field.key}`} type="text" value={personForm[field.key]}
                  onChange={(event) => setPersonForm((current) => ({ ...current, [field.key]: event.target.value }))}
                  className={inputClasses} />
              </div>
            ))}
          </div>
          {([
            { key: 'preferred_name', label: 'Preferred Name', type: 'text' },
            { key: 'primary_email', label: 'Primary Email', type: 'email' },
            { key: 'primary_phone', label: 'Primary Phone', type: 'text' },
          ] as const).map((field) => (
            <div key={field.key}>
              <label htmlFor={`person-${field.key}`} className={labelClasses}>{field.label}</label>
              <input id={`person-${field.key}`} type={field.type} value={personForm[field.key]}
                onChange={(event) => setPersonForm((current) => ({ ...current, [field.key]: event.target.value }))}
                className={inputClasses} />
            </div>
          ))}
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            Changing first or last name recomputes the full name.
          </p>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={contactModal !== null}
        title={contactModal === 'add' ? 'Add Contact Method' : 'Edit Contact Method'}
        onClose={() => setContactModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setContactModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={contactForm.contact_value.trim() === ''}
              loading={contactSaveMutation.isLoading} onClick={() => contactSaveMutation.mutate()}>
              Save Contact
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {contactModal === 'add' && (
            <div>
              <label htmlFor="contact-type" className={labelClasses}>Type</label>
              <select id="contact-type" value={contactForm.contact_type}
                onChange={(event) => setContactForm((current) => ({ ...current, contact_type: event.target.value }))}
                className={inputClasses}>
                {CONTACT_TYPES.map((option) => (
                  <option key={option} value={option}>{option.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="contact-value" className={labelClasses}>Value *</label>
            <input id="contact-value" type="text" value={contactForm.contact_value}
              onChange={(event) => setContactForm((current) => ({ ...current, contact_value: event.target.value }))}
              className={inputClasses} />
          </div>
          {contactForm.contact_type === 'social_media' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="contact-platform" className={labelClasses}>Platform</label>
                <input id="contact-platform" type="text" value={contactForm.platform}
                  placeholder="e.g. X, Facebook"
                  onChange={(event) => setContactForm((current) => ({ ...current, platform: event.target.value }))}
                  className={inputClasses} />
              </div>
              <div>
                <label htmlFor="contact-handle" className={labelClasses}>Handle</label>
                <input id="contact-handle" type="text" value={contactForm.handle}
                  onChange={(event) => setContactForm((current) => ({ ...current, handle: event.target.value }))}
                  className={inputClasses} />
              </div>
            </div>
          )}
          <div>
            <label htmlFor="contact-context" className={labelClasses}>Context</label>
            <select id="contact-context" value={contactForm.context}
              onChange={(event) => setContactForm((current) => ({ ...current, context: event.target.value }))}
              className={inputClasses}>
              {CONTACT_CONTEXTS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
            <input type="checkbox" checked={contactForm.is_primary}
              onChange={(event) => setContactForm((current) => ({ ...current, is_primary: event.target.checked }))} />
            Primary for this contact type
          </label>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={addressModal !== null}
        title={addressModal === 'add' ? 'Add Address' : 'Edit Address'}
        onClose={() => setAddressModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setAddressModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={addressForm.address_line_1.trim() === '' && addressForm.city.trim() === ''}
              loading={addressSaveMutation.isLoading} onClick={() => addressSaveMutation.mutate()}>
              Save Address
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {addressModal === 'add' && (
            <div>
              <label htmlFor="address-type" className={labelClasses}>Type</label>
              <select id="address-type" value={addressForm.address_type}
                onChange={(event) => setAddressForm((current) => ({ ...current, address_type: event.target.value }))}
                className={inputClasses}>
                {ADDRESS_TYPES.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="address-address_line_1" className={labelClasses}>Address Line 1</label>
            {/* Google Places suggestions; picking one fills city/state/ZIP too,
                keeping any part Google didn't return. */}
            <AddressAutocomplete
              id="address-address_line_1"
              value={addressForm.address_line_1}
              onChange={(value) => setAddressForm((current) => ({ ...current, address_line_1: value }))}
              onSelect={(parts) => setAddressForm((current) => ({
                ...current,
                city: parts.city || current.city,
                state: parts.state || current.state,
                zip_code: parts.zip || current.zip_code,
              }))}
              inputClassName={inputClasses}
              showIcon={false}
              placeholder=""
            />
          </div>
          <div>
            <label htmlFor="address-address_line_2" className={labelClasses}>Address Line 2</label>
            <input id="address-address_line_2" type="text" value={addressForm.address_line_2}
              onChange={(event) => setAddressForm((current) => ({ ...current, address_line_2: event.target.value }))}
              className={inputClasses} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {([
              { key: 'city', label: 'City' },
              { key: 'state', label: 'State' },
              { key: 'zip_code', label: 'ZIP' },
            ] as const).map((field) => (
              <div key={field.key}>
                <label htmlFor={`address-${field.key}`} className={labelClasses}>{field.label}</label>
                <input id={`address-${field.key}`} type="text" value={addressForm[field.key]}
                  onChange={(event) => setAddressForm((current) => ({ ...current, [field.key]: event.target.value }))}
                  className={inputClasses} />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary dark:text-text-dark-secondary">
            <input type="checkbox" checked={addressForm.is_primary}
              onChange={(event) => setAddressForm((current) => ({ ...current, is_primary: event.target.checked }))} />
            Primary for this address type
          </label>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deleteContactTarget !== null}
        title="Delete contact method"
        message={deleteContactTarget ? `Delete ${deleteContactTarget.label}? This cannot be undone.` : ''}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteContactTarget && contactDeleteMutation.mutate(deleteContactTarget.id)}
        onCancel={() => setDeleteContactTarget(null)}
      />

      <ConfirmationDialog
        isOpen={deleteAddressTarget !== null}
        title="Delete address"
        message={deleteAddressTarget ? `Delete the ${deleteAddressTarget.label} address? This cannot be undone.` : ''}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteAddressTarget && addressDeleteMutation.mutate(deleteAddressTarget.id)}
        onCancel={() => setDeleteAddressTarget(null)}
      />

      <AdminModal
        isOpen={committeeModalOpen}
        title={committeeInImport ? 'Change Finance Committee' : 'Edit Finance Committee'}
        onClose={() => setCommitteeModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setCommitteeModalOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                committeeInImport
                  ? committeeForm.sos_committee_id.trim() === ''
                  : committeeForm.name.trim() === '' && committeeForm.sos_committee_id.trim() === ''
              }
              loading={committeeSaveMutation.isLoading}
              onClick={() => committeeSaveMutation.mutate()}
            >
              Save Committee
            </Button>
          </>
        }
      >
        {committeeInImport ? (
          <div className="space-y-3">
            <div>
              <label htmlFor="committee-sos-link" className={labelClasses}>SOS Committee ID</label>
              <input id="committee-sos-link" type="text" value={committeeForm.sos_committee_id}
                onChange={(event) => setCommitteeForm((current) => ({ ...current, sos_committee_id: event.target.value }))}
                className={inputClasses} />
            </div>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Links this candidacy to a different TRACER committee. The committee's purpose, balance
              and agents are edited in Committees, where they're shared with everything linked to it.
            </p>
          </div>
        ) : (
        <div className="space-y-3">
          <div>
            <label htmlFor="committee-name" className={labelClasses}>Committee Name</label>
            <input id="committee-name" type="text" value={committeeForm.name}
              onChange={(event) => setCommitteeForm((current) => ({ ...current, name: event.target.value }))}
              className={inputClasses} />
          </div>
          <div>
            <label htmlFor="committee-sos" className={labelClasses}>SOS Committee ID</label>
            <input id="committee-sos" type="text" value={committeeForm.sos_committee_id}
              onChange={(event) => setCommitteeForm((current) => ({ ...current, sos_committee_id: event.target.value }))}
              className={inputClasses} />
          </div>
          <div>
            <label htmlFor="committee-beginning-balance" className={labelClasses}>Beginning Balance</label>
            <input id="committee-beginning-balance" type="number" step="0.01" inputMode="decimal"
              value={committeeForm.beginning_balance}
              onChange={(event) => setCommitteeForm((current) => ({ ...current, beginning_balance: event.target.value }))}
              className={inputClasses} />
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Funds carried into the current election cycle, before any imported contributions.
              Added to current-cycle totals. Leave blank if unknown — that is not the same as 0,
              and imports never overwrite it.
            </p>
          </div>
          <div>
            <span className={labelClasses}>Registered Agent</span>
            <SearchSelect
              placeholder="Search people…"
              fetchOptions={searchPersons}
              selected={registeredAgent}
              onSelect={setRegisteredAgent}
              onClear={() => setRegisteredAgent(null)}
              ariaLabel="Search registered agent"
            />
          </div>
          <div>
            <span className={labelClasses}>Designated Filing Agent</span>
            <SearchSelect
              placeholder="Search people…"
              fetchOptions={searchPersons}
              selected={designatedAgent}
              onSelect={setDesignatedAgent}
              onClear={() => setDesignatedAgent(null)}
              ariaLabel="Search designated filing agent"
            />
          </div>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            A committee needs a name or an SOS committee ID. Agents are person records — create them
            from the People list first if they don't exist yet.
          </p>
        </div>
        )}
      </AdminModal>

      <AdminModal
        isOpen={staffModal !== null}
        title={staffModal === 'add' ? 'Add Campaign Staff' : 'Edit Staff Role'}
        onClose={() => setStaffModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setStaffModal(null)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              disabled={staffModal === 'add' && !staffPerson}
              loading={staffSaveMutation.isLoading}
              onClick={() => staffSaveMutation.mutate()}
            >
              Save Staff Role
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {staffModal === 'add' ? (
            <div>
              <span className={labelClasses}>Person *</span>
              <SearchSelect
                placeholder="Search people…"
                fetchOptions={searchPersons}
                selected={staffPerson}
                onSelect={setStaffPerson}
                onClear={() => setStaffPerson(null)}
                ariaLabel="Search staff person"
              />
            </div>
          ) : (
            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
              {staffPerson?.label}
            </p>
          )}
          <div>
            <label htmlFor="staff-role-type" className={labelClasses}>Role</label>
            <select id="staff-role-type" value={staffForm.role_type}
              onChange={(event) => setStaffForm((current) => ({ ...current, role_type: event.target.value }))}
              className={inputClasses}>
              {STAFF_ROLE_TYPES.map((option) => (
                <option key={option} value={option}>{roleTypeLabel(option)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="staff-role-title" className={labelClasses}>Title (optional)</label>
            <input id="staff-role-title" type="text" value={staffForm.role_title}
              onChange={(event) => setStaffForm((current) => ({ ...current, role_title: event.target.value }))}
              className={inputClasses} />
          </div>
          {staffModal === 'edit' && (
            <div>
              <label htmlFor="staff-status" className={labelClasses}>Status</label>
              <select id="staff-status" value={staffForm.status}
                onChange={(event) => setStaffForm((current) => ({ ...current, status: event.target.value }))}
                className={inputClasses}>
                {STAFF_STATUSES.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={removeStaffTarget !== null}
        title="Remove staff member"
        message={
          removeStaffTarget
            ? `Remove ${removeStaffTarget.name} from this candidacy's staff? The role record is deleted.`
            : ''
        }
        confirmText="Remove"
        variant="danger"
        onConfirm={() => removeStaffTarget && staffRemoveMutation.mutate(removeStaffTarget.id)}
        onCancel={() => setRemoveStaffTarget(null)}
      />
    </AdminPageShell>
  );
};

export default PeoplePersonDetailPage;
